import { useFrame, useThree } from "@react-three/fiber";
import { useRef } from "react";
import { Vector3 } from "three";
import { useScroll } from "../scroll/ScrollProvider";

/**
 * 13 sections:
 *   0 Hero · 1 Mandate · 2 Bullion · 3 Digital · 4 Banking (Earth) ·
 *   5 Cognitive (Brain) · 6 AI Integration · 7 Data Centers · 8 Power Equipment ·
 *   9 Carbon Credits · 10 Bhutan FDI · 11 Network (Constellation) · 12 Contact
 *
 * Anchors in QuantumScene:
 *   Hero z=0, Gold z=-14, Coin z=-28, Earth z=-42, Brain z=-56,
 *   Compute z=-66, DataCenter z=-72, Constellation z=-82, Bhutan z=-88
 */
const WAYPOINTS: { pos: [number, number, number]; look: [number, number, number] }[] = [
  // 0 — Hero
  { pos: [0, 0.2, 6], look: [0, 0, 0] },
  // 1 — Mandate
  { pos: [-0.8, 0.4, -3], look: [0.4, 0, -8] },
  // 2 — Bullion: dragon coins
  { pos: [0.0, 0.1, -8.0], look: [0, 0, -14] },
  // 3 — Digital coin
  { pos: [-0.4, 0.4, -22.5], look: [0, 0, -28] },
  // 4 — Banking (Earth): wide framing, slight off-axis
  { pos: [1.0, 0.4, -38], look: [0, 0, -42] },
  // 5 — Cognitive (Brain)
  { pos: [-0.4, 0.4, -52], look: [0, 0, -56] },
  // 6 — AI Integration / compute core zoom-in
  { pos: [0.7, 0.35, -62.8], look: [0, 0, -66] },
  // 7 — Data center aisle zoom-in
  { pos: [0.95, 0.2, -68.8], look: [0.1, 0, -72] },
  // 8 — Power infrastructure bridge
  { pos: [0.45, 0.24, -74.8], look: [0, 0, -78] },
  // 9 — Carbon Credits transition
  { pos: [0.15, 0.26, -79.5], look: [0, 0.1, -83] },
  // 10 — Bhutan FDI tribute
  { pos: [-0.15, 0.28, -84.2], look: [0, 0.2, -88] },
  // 11 — Network (Constellation)
  { pos: [0, 0.4, -78], look: [0, 0, -82] },
  // 12 — Contact
  { pos: [0, 0, -94], look: [0, 0, -100] },
];

const tmpA = new Vector3();
const tmpB = new Vector3();
const tmpLookA = new Vector3();
const tmpLookB = new Vector3();
const targetPos = new Vector3();
const targetLook = new Vector3();
const currentLook = new Vector3();

export function CameraRig() {
  const { camera, pointer } = useThree();
  const { progress } = useScroll();
  const progressRef = useRef(progress);
  progressRef.current = progress;

  useFrame((_state, delta) => {
    const p = progressRef.current;
    const segments = WAYPOINTS.length - 1;
    const f = p * segments;
    const i = Math.min(segments - 1, Math.floor(f));
    const t = ease(f - i);

    const a = WAYPOINTS[i];
    const b = WAYPOINTS[i + 1];

    tmpA.set(...a.pos);
    tmpB.set(...b.pos);
    targetPos.copy(tmpA).lerp(tmpB, t);

    tmpLookA.set(...a.look);
    tmpLookB.set(...b.look);
    targetLook.copy(tmpLookA).lerp(tmpLookB, t);

    const parallaxStrength = 0.6 * (1 - Math.min(1, p * 1.5));
    targetPos.x += pointer.x * parallaxStrength;
    targetPos.y += pointer.y * parallaxStrength * 0.5;

    const k = 1 - Math.exp(-delta * 5);
    camera.position.lerp(targetPos, k);
    currentLook.lerp(targetLook, k);
    camera.lookAt(currentLook);
  });

  return null;
}

function ease(t: number) {
  return t * t * (3 - 2 * t);
}
